import { cn } from '@/shared/lib/utils/classnames';
import * as Icons from './icons';

/**
 * Tool 이름 타입
 * icons 폴더의 모든 아이콘과 매칭
 */
export type ToolName =
  | 'airtable'
  | 'chatgpt'
  | 'claude'
  | 'confluence'
  | 'discord'
  | 'excel'
  | 'function'
  | 'gmail'
  | 'google-calendar'
  | 'google-docs'
  | 'google-drive'
  | 'google-search'
  | 'google-sheets'
  | 'jira'
  | 'microsoft-onedrive'
  | 'microsoft-planner'
  | 'microsoft-powerpoint'
  | 'microsoft-teams'
  | 'microsoft-word'
  | 'notion'
  | 'outlook'
  | 'sharepoint'
  | 'slack'
  | 'youtube';

/**
 * Tool 아이콘 컴포넌트 Props
 */
export interface ToolIconProps {
  /** Tool 이름 */
  name: ToolName;
  /** Border 스타일 variant */
  variant?: 'default';
  /** 커스텀 className (border 스타일 커스터마이징) */
  className?: string;
  /** 아이콘 크기 */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Tool 아이콘 맵핑
 */
const toolIconMap: Record<ToolName, React.ComponentType<{ className?: string }>> = {
  airtable: Icons.AirtableIcon,
  chatgpt: Icons.ChatGptIcon,
  claude: Icons.ClaudeIcon,
  confluence: Icons.ConfluenceIcon,
  discord: Icons.DiscordIcon,
  excel: Icons.ExcelIcon,
  function: Icons.FunctionIcon,
  gmail: Icons.GmailIcon,
  'google-calendar': Icons.GoogleCalendarIcon,
  'google-docs': Icons.GoogleDocsIcon,
  'google-drive': Icons.GoogleDriveIcon,
  'google-search': Icons.GoogleSearchIcon,
  'google-sheets': Icons.GoogleSheetsIcon,
  jira: Icons.JiraIcon,
  'microsoft-onedrive': Icons.MicrosoftOneDriveIcon,
  'microsoft-planner': Icons.MicrosoftPlannerIcon,
  'microsoft-powerpoint': Icons.MicrosoftPowerPointIcon,
  'microsoft-teams': Icons.MicrosoftTeamsIcon,
  'microsoft-word': Icons.MicrosoftWordIcon,
  notion: Icons.NotionIcon,
  outlook: Icons.OutlookIcon,
  sharepoint: Icons.SharePointIcon,
  slack: Icons.SlackIcon,
  youtube: Icons.YoutubeIcon,
};

/**
 * Border 스타일 variant 맵핑
 */
const borderVariants = {
  default: 'border border-label-disabled rounded-[4px] p-[2px] bg-white',
};

/**
 * 아이콘 크기 맵핑
 */
const iconSizes = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
} as const;

/**
 * ToolIcon 컴포넌트
 *
 * Tool 아이콘을 border로 감싼 형태로 표시합니다.
 * Border 스타일은 variant나 className으로 커스터마이징 가능합니다.
 *
 * @example
 * ```tsx
 * <ToolIcon name="chatgpt" size="sm" />
 * <ToolIcon name="slack" size="lg" className="border-2 border-primary" />
 * ```
 */
export function ToolIcon({ name, variant = 'default', size = 'md', className }: ToolIconProps) {
  const IconComponent = toolIconMap[name];

  if (!IconComponent) {
    console.warn(`ToolIcon: Unknown tool name "${name}"`);
    return null;
  }

  return (
    <div
      className={cn('inline-flex items-center justify-center', borderVariants[variant], className)}
    >
      <IconComponent className={iconSizes[size]} />
    </div>
  );
}
